import React, { useState } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import * as Icons from 'lucide-react';
import type { Notification } from '@/types/notifications';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onSnooze?: (id: string, minutes: number) => void;
  onActionClick?: (notificationId: string, actionId: string) => void;
  isGrouped?: boolean;
}

/**
 * Luxury notification card with glassmorphic design
 * Features: Priority color coding, swipe gestures, smooth animations, action buttons
 */
export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onSnooze,
  onActionClick,
  isGrouped = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-150, 0, 150], [0.5, 1, 0.5]);

  // Get relative time
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  // Get app icon component
  const getAppIcon = () => {
    const iconName = notification.app_icon || 'Bell';
    const IconComponent = (Icons as any)[iconName] || Icons.Bell;
    return <IconComponent className="w-4 h-4" />;
  };

  // Priority color system with glows
  const getPriorityStyles = () => {
    switch (notification.priority) {
      case 'urgent':
        return {
          border: 'border-l-red-500',
          glow: 'shadow-[0_0_15px_rgba(239,68,68,0.3)]',
          badge: 'bg-red-500/20 text-red-400 border-red-500/30',
        };
      case 'high':
        return {
          border: 'border-l-orange-500',
          glow: 'shadow-[0_0_15px_rgba(249,115,22,0.3)]',
          badge: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
        };
      case 'low':
        return {
          border: 'border-l-gray-500',
          glow: '',
          badge: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        };
      default: // 'default'
        return {
          border: 'border-l-blue-500',
          glow: 'shadow-[0_0_12px_rgba(59,130,246,0.25)]',
          badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        };
    }
  };

  const priorityStyles = getPriorityStyles();

  // Handle swipe gestures
  const handleDragEnd = (event: any, info: PanInfo) => {
    const threshold = 100;
    
    if (info.offset.x > threshold) {
      // Swipe right: Mark as read
      onMarkAsRead?.(notification.id);
    } else if (info.offset.x < -threshold) {
      // Swipe left: Dismiss
      onDismiss?.(notification.id);
    }
  };

  // Handle action button clicks
  const handleActionClick = (actionId: string) => {
    onActionClick?.(notification.id, actionId);
  };

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      style={{ x, opacity }}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.2 }}
      whileHover={{ scale: 1.01 }}
      className={cn(
        'group relative rounded-xl p-4 border-l-4 transition-all duration-200 cursor-pointer',
        priorityStyles.border,
        priorityStyles.glow,
        !notification.is_read && 'bg-white/5',
        notification.is_read && 'bg-white/[0.02] opacity-70',
        // Glassmorphic effect
        'backdrop-blur-xl',
        'border border-white/10',
        'hover:bg-white/8 hover:border-white/20',
        isGrouped && 'ml-4'
      )}
      onClick={() => {
        if (!notification.is_read) onMarkAsRead?.(notification.id);
        setIsExpanded(!isExpanded);
      }}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-3 right-3 w-2 h-2 rounded-full bg-blue-500"
        >
          <motion.div
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 rounded-full bg-blue-400"
          />
        </motion.div>
      )}

      <div className="flex items-start gap-3">
        {/* Avatar or App Icon */}
        <div className="flex-shrink-0 relative">
          {notification.avatar_url ? (
            <Avatar className="w-10 h-10 border-2 border-white/20">
              <AvatarImage src={notification.avatar_url} alt="User" />
              <AvatarFallback className="bg-gradient-to-br from-blue-500/30 to-purple-500/30 text-white text-sm">
                {notification.title.charAt(0)}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white/10 to-white/5 border border-white/20 flex items-center justify-center text-white/70">
              {getAppIcon()}
            </div>
          )}

          {/* App badge overlay */}
          {notification.app_icon && notification.avatar_url && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-white/20 flex items-center justify-center">
              <div className="w-3 h-3 text-white/80">
                {getAppIcon()}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header: Title and Time */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-semibold text-white leading-tight">
              {notification.title}
            </h4>
            
            <div className="flex items-center gap-1">
              <span className="text-xs text-white/50 whitespace-nowrap">
                {timeAgo}
              </span>
              
              {/* Individual Action Buttons */}
              <div className="flex items-center gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {!notification.is_read && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsRead?.(notification.id);
                    }}
                    className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-white transition-colors"
                    title="Mark as read"
                  >
                    <Icons.Check className="w-3.5 h-3.5" />
                  </button>
                )}
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss?.(notification.id);
                  }}
                  className="p-1 hover:bg-red-500/20 rounded text-white/60 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Icons.Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          {notification.body && (
            <p className={cn(
              'text-sm text-white/70 mb-2 leading-relaxed',
              !isExpanded && 'line-clamp-2'
            )}>
              {notification.body}
            </p>
          )}

          {/* Metadata Row */}
          <div className="flex items-center gap-2 text-xs text-white/50 mb-3">
            {notification.app_name && (
              <>
                <span className="font-medium text-white/60">{notification.app_name}</span>
                {(notification.category || notification.priority !== 'default') && <span>•</span>}
              </>
            )}
            {notification.category && notification.category !== 'general' && (
              <>
                <span className="capitalize">{notification.category}</span>
              </>
            )}
            {notification.priority && notification.priority !== 'default' && (
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium border',
                priorityStyles.badge
              )}>
                {notification.priority}
              </span>
            )}
          </div>

          {/* Progress Bar (if media type is progress) */}
          {notification.media?.type === 'progress' && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-white/60">Progress</span>
                <span className="text-xs text-white/60">{notification.media.value}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${notification.media.value}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {(notification.actions && notification.actions.length > 0) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex flex-wrap gap-2 mt-3"
            >
              {notification.actions.map((action) => {
                const ActionIcon = action.icon ? (Icons as any)[action.icon] : null;
                
                return (
                  <Button
                    key={action.id}
                    variant={action.variant || 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActionClick(action.id);
                    }}
                    className={cn(
                      'text-xs h-8 px-3',
                      'backdrop-blur-xl',
                      'transition-all duration-200',
                      'hover:scale-105',
                      action.variant === 'default' && 'bg-blue-600/90 hover:bg-blue-500/90 text-white border-blue-500/50',
                      action.variant === 'outline' && 'bg-white/5 hover:bg-white/10 text-white/80 border-white/20 hover:border-white/30',
                      action.variant === 'destructive' && 'bg-red-600/90 hover:bg-red-500/90 text-white border-red-500/50',
                    )}
                  >
                    {ActionIcon && <ActionIcon className="w-3 h-3 mr-1.5" />}
                    {action.label}
                  </Button>
                );
              })}
            </motion.div>
          )}

          {/* File Attachments */}
          {notification.metadata?.files && Array.isArray(notification.metadata.files) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-3 space-y-2"
            >
              {notification.metadata.files.map((file: any, index: number) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
                >
                  <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center">
                    <Icons.FileText className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{file.name}</p>
                    <p className="text-xs text-white/50">{file.size}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-white/70 hover:text-white h-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle download
                    }}
                  >
                    <Icons.Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Swipe hint (shows on hover for unread notifications) */}
      {!notification.is_read && (
        <motion.div
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          className="absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-white/40 pointer-events-none"
        >
          <span className="hidden group-hover:block">
            ← Swipe to mark read | Swipe to dismiss →
          </span>
        </motion.div>
      )}

      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
    </motion.div>
  );
};
